import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, InternalStandardProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import debounce from "lodash-es/debounce";

import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import { VideoDexie, AudioDexie } from "JS/Database/Dexie";
import { useGlobalVideos } from "JS/React/Hooks/Video";
import { useGlobalAudios } from "JS/React/Hooks/Audio";
import { useVideoIndexFromDb } from "JS/React/Hooks/Database/Video";
import { useMemberAudiosFromDb } from "JS/React/Hooks/Database/Audio";
import { useScrollWithoutDelay } from "JS/React/Hooks/Media";
import { Content, ReceivedGift } from "JS/Models";
import {
    useReceivedMemberAudioGifts,
    useReceivedMemberVideoGifts,
} from "JS/React/Hooks/Gifts";
import { getUniqueValues, sortArrayByKey } from "JS/Helpers";
import { InfiniteScrollManager } from "JS/React/Container/InfiniteScrollManager";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { OfflineListingComponent } from "./OfflineListingComponent";

const RootDiv = styled("div")({});

const ContentTypography = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    color: `${theme.palette.common.black}`,
}));

export const DownloadsOffline = (props: DownloadsOfflineProps) => {
    const navigate = useNavigate();
    const { handleRedirectToDetail } = useHandlers();
    const { linkProvider } = useRouting();
    const { className } = props;
    const params: any = useParams();
    const { category: selectedTab } = params;
    const {
        globalAudios,
        memberAudios,
        receivedMemberAudios,
        audiosLoading,
        isResumeableAudios,
    } = useOfflineAudios(selectedTab);
    const {
        globalVideos,
        memberVideos,
        receivedMemberVideos,
        videosLoading,
        isResumeableVideos,
    } = useOfflineVideos(selectedTab);
    useScrollWithoutDelay();
    const onBackClick = () =>
        navigate(linkProvider.react.offline().index());

    const downloadedSkuIds: string[] = useMemo(() => {
        if (selectedTab === Tabs.AUDIOS)
            return memberAudios.map((x) => x.skuId);

        if (selectedTab === Tabs.VIDEOS)
            return memberVideos.map((x) => x.skuId);

        return [];
    }, [memberAudios, memberVideos, selectedTab]);

    const nonDownloadedContent = useMemo(() => {
        if (selectedTab === Tabs.AUDIOS) {
            const sorted = sortArrayByKey(
                [
                    ...getUniqueValues(globalAudios, "nid"),
                    ...getUniqueValues(receivedMemberAudios, "nid"),
                ],
                "title",
                "ASC",
            );
            return sorted.filter((x) => !downloadedSkuIds.includes(x.sku_id));
        }
        if (selectedTab === Tabs.VIDEOS) {
            const sorted = sortArrayByKey(
                [
                    ...getUniqueValues(globalVideos, "nid"),
                    ...getUniqueValues(receivedMemberVideos, "nid"),
                ],
                "title",
                "ASC",
            );
            return sorted.filter((x) => !downloadedSkuIds.includes(x.sku_id));
        }
    }, [
        globalAudios,
        globalVideos,
        downloadedSkuIds,
        selectedTab,
        receivedMemberAudios,
        receivedMemberVideos,
    ]);

    const allAudios = useMemo(() => {
        const sorted = sortArrayByKey(memberAudios, "name", "ASC");
        return [...sorted, ...nonDownloadedContent];
    }, [memberAudios, nonDownloadedContent]);

    const allVideos = useMemo(() => {
        const sorted = sortArrayByKey(memberVideos, "name", "ASC");
        return [...sorted, ...nonDownloadedContent];
    }, [memberVideos, nonDownloadedContent]);

    const {
        finalAudioData,
        finalVideoData,
        debSearch,
        debouncedSearch,
        setDebSearch,
    } = useContentFilters(allAudios, allVideos, selectedTab);

    const downloadedLoading = videosLoading || audiosLoading;

    const noItemsToShow = useMemo(() => {
        if (selectedTab === Tabs.VIDEOS) return finalVideoData?.length === 0;
        return finalAudioData?.length === 0;
    }, [selectedTab, finalAudioData, finalVideoData]);
    function renderOfflineListingComponent() {
        const isAudioTab = selectedTab === Tabs.AUDIOS;

        return (
            <OfflineListingComponent
                data={isAudioTab ? finalAudioData : finalVideoData}
                handleRedirectToDetail={handleRedirectToDetail}
                searchQuery={debSearch}
                isResumeableContent={
                    isAudioTab ? isResumeableAudios : isResumeableVideos
                }
                controlType={isAudioTab ? "audio" : "video"}
            />
        );
    }
    return (
        <RootDiv className={className}>
            <AppHeader
                title={selectedTab}
                canGoBack
                showTabs={false}
                onBackClick={onBackClick}
                searchQuery={debSearch}
                setSearchQuery={(val) => {
                    setDebSearch(val);
                    debouncedSearch(val);
                }}
                searchPlaceholder={"Search by title/description"}
            />

            {!downloadedLoading && noItemsToShow && (
                <ContentTypography
                    align="center"
                    variant="h6"
                >
                    {messaging.common.noItemsFound}
                </ContentTypography>
            )}

            {downloadedLoading && (
                <Box display={"flex"} justifyContent="center">
                    <AppCircularProgress />
                </Box>
            )}

            {!downloadedLoading && (
                <InfiniteScrollManager>
                    {renderOfflineListingComponent()}
                </InfiniteScrollManager>
            )}
        </RootDiv>
    );
};

const useOfflineAudios = (selectedTab: Tabs) => {
    const {
        resumeUserAudios,
        resumeUserBundles,
        audios: globalAudios,
    } = useGlobalAudios();
    const { memberAudios, loading: audiosLoading } = useMemberAudiosFromDb(
        selectedTab !== Tabs.AUDIOS,
    );

    const { receivedMemberAudios } = useReceivedMemberAudioGifts(true);

    const isResumeableAudios = (filterAudio: AudioDexie) => {
        const contentNid = filterAudio?.id.split("-")[1];
        if (filterAudio.isBundle) {
            let filterResumeBundle =
                resumeUserBundles &&
                resumeUserBundles.filter((x) => x.nid === contentNid);
            return filterResumeBundle.length > 0;
        } else {
            if (
                resumeUserAudios &&
                resumeUserAudios.filter((x) => x.nid === contentNid).length > 0
            ) {
                const resumeData = resumeUserAudios.find(
                    (x) => x.nid === contentNid,
                );
                return !!resumeData;
            } else {
                return false;
            }
        }
    };

    return {
        globalAudios,
        memberAudios,
        receivedMemberAudios,
        audiosLoading,
        isResumeableAudios,
    };
};

const useOfflineVideos = (selectedTab: Tabs) => {
    const { memberVideos, loading: videosLoading } = useVideoIndexFromDb(
        selectedTab !== Tabs.VIDEOS,
    );
    const { receivedMemberVideos } = useReceivedMemberVideoGifts(true);

    const { resumeUserVideos, videos: globalVideos } = useGlobalVideos();

    const isResumeableVideos = (filteredVideo: VideoDexie) => {
        const contentNid = filteredVideo?.id.split("-")[1];
        if (
            resumeUserVideos &&
            resumeUserVideos.filter((x) => x.nid === contentNid).length > 0
        ) {
            const resumeData = resumeUserVideos.find(
                (x) => x.nid === contentNid,
            );
            return !!resumeData;
        } else {
            return false;
        }
    };

    return {
        memberVideos,
        receivedMemberVideos,
        globalVideos,
        videosLoading,
        isResumeableVideos,
    };
};

const useContentFilters = (
    allAudios: (Content | AudioDexie | ReceivedGift)[],
    allVideos: (Content | VideoDexie | ReceivedGift)[],
    selectedTab: Tabs,
) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [debSearch, setDebSearch] = useState("");

    const match = (toMatch: string) => {
        return toMatch
            ?.toLocaleLowerCase()
            .includes(searchQuery.toLocaleLowerCase().trim());
    };

    const searchedAudios = useMemo(() => {
        return allAudios?.filter(
            (d) =>
                match((d as Content).title) ||
                match(d.description) ||
                match((d as AudioDexie).name),
        );
    }, [allAudios, searchQuery]);

    const searchedVideos = useMemo(() => {
        return allVideos?.filter(
            (d) =>
                match((d as Content).title) ||
                match(d.description) ||
                match((d as VideoDexie).name),
        );
    }, [allVideos, searchQuery]);

    const finalAudioData = useMemo(() => {
        return searchedAudios;
    }, [searchedAudios]);

    const finalVideoData = useMemo(() => {
        return searchedVideos;
    }, [searchedVideos]);

    const debouncedSearch = useMemo(() => {
        return debounce(
            (val) => {
                setSearchQuery(val);
            },
            500,
            {
                leading: false,
                trailing: true,
            },
        );
    }, []);

    return {
        debSearch,
        finalAudioData,
        finalVideoData,
        debouncedSearch,
        setDebSearch,
    };
};

const useHandlers = () => {
    const { linkProvider } = useRouting();
    const navigate = useNavigate();
    const handleRedirectToDetail = (
        nid: string,
        type?: string,
        isAutoPlay: boolean = false,
        scrollTo: number = 0,
    ) => {
        navigate(
            linkProvider.react
                .offline()
                .downloaddetail(nid, type, `${isAutoPlay}`, {
                    scrollTo: +scrollTo,
                }),
        );
    };

    return { handleRedirectToDetail };
};

export enum Tabs {
    AUDIOS = "Audios",
    VIDEOS = "Videos",
}

export interface DownloadsOfflineProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}
